import { DeviceFormFactor, UserAgentStats, UserAgentGenericStats } from '@dev/ee-api/ee-api-models';
import { palette, DetailedTableRowsProps, MobileCardRowsProps, MobileDetailedTableSingleRow } from '@dev/ee-components';
import { sanitizeStringForTranslation } from '@dev/ee-tools';
import { forEach, map } from 'lodash-es';
import { useTranslation } from 'react-i18next';
import { formatNumber } from '@/services/tools/formatNumber';
import { DeviceToggleBtnValues, DetailedRow, RadioValues } from '../../DeviceDetailsInterface';

export const useCreateRows = (data, toggleBtnValue): DetailedTableRowsProps[] => {
    const { t } = useTranslation();

    if (!data) return [];
    const availableColors = {
        [DeviceFormFactor.Desktop]: palette.indigo[8],
        [DeviceFormFactor.Mobile]: palette.indigo[6],
        [DeviceFormFactor.Tablet]: palette.indigo[4],
        [DeviceFormFactor.Other]: palette.indigo[2],
    };

    return map(data, (row): DetailedTableRowsProps => {
        const opened = getOpenedStats(row, toggleBtnValue);
        const unsubscribed = getUnsubscribeStats(row, toggleBtnValue);
        const clicked = getClickedStats(row, toggleBtnValue);
        const details = createSubRows(row, toggleBtnValue, t);

        return {
            id: row.FormFactorName,
            name: t(sanitizeStringForTranslation(row.FormFactorName), row.FormFactorName),
            opened,
            unsubscribed,
            clicked,
            color: availableColors[row.FormFactor],
            details,
        };
    });
};

const createSubRows = (row, toggleBtnValue, t: any): DetailedRow[] =>
    map(
        row.Items,
        (subrow): Record<string, any> => ({
            name: t(sanitizeStringForTranslation(subrow.Name), subrow.Name),
            opened:
                toggleBtnValue === DeviceToggleBtnValues.Number
                    ? formatNumber(subrow.OpenCount)
                    : `${formatNumber(subrow.OpenRate)}%`,
            unsubscribed:
                toggleBtnValue === DeviceToggleBtnValues.Number
                    ? formatNumber(subrow.UnsubscribeCount || 0)
                    : `${formatNumber(subrow.UnsubscribeRate || 0)}%`,
            clicked:
                toggleBtnValue === DeviceToggleBtnValues.Number
                    ? formatNumber(subrow.ClickCount)
                    : `${formatNumber(subrow.ClickRate)}%`,
        })
    );

const getUnsubscribeStats = (row, toggleBtnValue): number | string =>
    toggleBtnValue === DeviceToggleBtnValues.Number
        ? row.UnsubscribeCount === null
            ? 'N/A'
            : formatNumber(row.UnsubscribeCount)
        : row.UnsubscribeRate === null
        ? 'N/A'
        : `${formatNumber(row.UnsubscribeRate)}%`;

const getClickedStats = (row, toggleBtnValue): number | string =>
    toggleBtnValue === DeviceToggleBtnValues.Number ? formatNumber(row.ClickCount) : `${formatNumber(row.ClickRate)}%`;

const getOpenedStats = (row, toggleBtnValue): number | string =>
    toggleBtnValue === DeviceToggleBtnValues.Number ? formatNumber(row.OpenCount) : `${formatNumber(row.OpenRate)}%`;

export const useCreateMobileRows = (data, toggleBtnValue, radioValue): MobileDetailedTableSingleRow[] => {
    const { t } = useTranslation();
    if (!data) return [];

    const availableColors = {
        [DeviceFormFactor.Desktop]: palette.indigo[8],
        [DeviceFormFactor.Mobile]: palette.indigo[6],
        [DeviceFormFactor.Tablet]: palette.indigo[4],
        [DeviceFormFactor.Other]: palette.indigo[2],
    };

    const createMobileHeaders = (row: UserAgentStats): string[] => {
        const header = [];
        map(row.Items, (subrow) => header.push(t(subrow.Name.toLowerCase(), subrow.Name)));
        return header;
    };

    return map(data, (row: UserAgentStats): MobileDetailedTableSingleRow => {
        const opened = getOpenedStats(row, toggleBtnValue);
        const unsubscribed = getUnsubscribeStats(row, toggleBtnValue);
        const clicked = getClickedStats(row, toggleBtnValue);
        const headers = createMobileHeaders(row);
        let agregatedDetail;

        switch (radioValue) {
            case RadioValues.Clicked:
                agregatedDetail = clicked;
                break;
            case RadioValues.Opened:
                agregatedDetail = opened;
                break;
            case RadioValues.Unsubscribed:
                agregatedDetail = unsubscribed;
                break;
            default:
                break;
        }

        const detailsRows =
            agregatedDetail !== '0' && agregatedDetail !== '0%' && agregatedDetail !== 'N/A'
                ? createMobileSubRows(row, toggleBtnValue, radioValue)
                : null;

        return {
            id: row.FormFactorName,
            name: t(sanitizeStringForTranslation(row.FormFactorName), row.FormFactorName),
            agregatedDetail,
            color: availableColors[row.FormFactor],
            headers,
            detailsRows,
        };
    });
};

const createMobileSubRows = (row, toggleBtnValue, radioValue): MobileCardRowsProps => {
    const detailedRows = {};
    forEach(row.Items, (subrow: UserAgentGenericStats): void => {
        if (radioValue === RadioValues.Opened) {
            detailedRows[subrow.Name] =
                toggleBtnValue === DeviceToggleBtnValues.Number
                    ? formatNumber(subrow.OpenCount)
                    : `${formatNumber(subrow.OpenRate)}%`;
        } else if (radioValue === RadioValues.Clicked) {
            detailedRows[subrow.Name] =
                toggleBtnValue === DeviceToggleBtnValues.Number
                    ? formatNumber(subrow.ClickCount)
                    : `${formatNumber(subrow.ClickRate)}%`;
        } else if (radioValue === RadioValues.Unsubscribed) {
            detailedRows[subrow.Name] =
                toggleBtnValue === DeviceToggleBtnValues.Number
                    ? formatNumber(subrow.UnsubscribeCount || 0)
                    : `${formatNumber(subrow.UnsubscribeRate || 0)}%`;
        }
    });

    return detailedRows;
};
